
import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Check, Trash2, Plus, ListTodo } from "lucide-react";

interface VehicleWishlistProps {
  vehicleId: string;
}

interface Wish {
  id: string;
  description: string;
  completed: boolean;
}

export const VehicleWishlist = ({ vehicleId }: VehicleWishlistProps) => {
  const [newWish, setNewWish] = useState("");
  const { toast } = useToast();

  const { data: wishes, refetch } = useQuery({
    queryKey: ["vehicle-wishes", vehicleId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("vehicle_wishes")
        .select("*")
        .eq("vehicle_id", vehicleId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as Wish[];
    },
  });

  const addWish = async () => {
    if (!newWish.trim()) return;

    try {
      const { error } = await supabase
        .from("vehicle_wishes")
        .insert({ vehicle_id: vehicleId, description: newWish.trim() });

      if (error) throw error;

      toast({
        title: "Wens toegevoegd",
        description: "Je wens is succesvol toegevoegd aan de lijst.",
      });

      setNewWish("");
      refetch();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Fout bij toevoegen",
        description: error.message,
      });
    }
  };

  const toggleWish = async (wishId: string, completed: boolean) => {
    try {
      const { error } = await supabase
        .from("vehicle_wishes")
        .update({ completed: !completed })
        .eq("id", wishId);

      if (error) throw error;
      refetch();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Fout bij bijwerken",
        description: error.message,
      });
    }
  };

  const deleteWish = async (wishId: string) => {
    try {
      const { error } = await supabase
        .from("vehicle_wishes")
        .delete()
        .eq("id", wishId);

      if (error) throw error;

      toast({
        title: "Wens verwijderd",
        description: "Je wens is succesvol verwijderd van de lijst.",
      });

      refetch();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Fout bij verwijderen",
        description: error.message,
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <ListTodo className="h-5 w-5 text-[#B1A06D]" />
          <CardTitle className="text-xl font-semibold">Wensenlijst</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Textarea
            placeholder="Voeg een nieuwe wens toe..."
            value={newWish}
            onChange={(e) => setNewWish(e.target.value)}
            className="min-h-[60px]"
          />
          <Button
            className="w-full"
            onClick={addWish}
            disabled={!newWish.trim()}
          >
            <Plus className="w-4 h-4 mr-2" />
            Toevoegen
          </Button>
        </div>

        <div className="space-y-2">
          {wishes?.map((wish) => (
            <div
              key={wish.id}
              className="flex items-start justify-between gap-2 p-3 bg-gray-50 rounded-lg"
            >
              <span
                className={`flex-1 ${
                  wish.completed ? "text-gray-500 line-through" : ""
                }`}
              >
                {wish.description}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={() => toggleWish(wish.id, wish.completed)}
                >
                  <Check
                    className={`h-4 w-4 ${
                      wish.completed ? "text-green-500" : "text-gray-400"
                    }`}
                  />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={() => deleteWish(wish.id)}
                >
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              </div>
            </div>
          ))}
          {wishes?.length === 0 && (
            <p className="text-center text-gray-500 py-4">
              Nog geen wensen toegevoegd
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

